import { styled } from 'baseui'
import { FlexColumn, FlexBetween, FlexCenterY } from '@/shared/theme/styled-components'

/** @typedef {import('react').ComponentType<{ $pulse?: boolean, children?: import('react').ReactNode }>} PulseLayoutBlock */

/** @type {PulseLayoutBlock} */
export const LayoutContainer = styled(FlexColumn, ({ $theme, $pulse }) => ({
  height: '100%',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: $pulse ? 'transparent' : $theme.colors.white,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
}))

/** @type {PulseLayoutBlock} */
export const ContentWrapper = styled('div', ({ $pulse }) => ({
  display: 'flex',
  width: '100%',
  boxSizing: 'border-box',
  gap: $pulse ? '20px' : '2rem',
  ...($pulse && { alignItems: 'flex-start' }),
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  marginTop: $pulse ? '16px' : 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
}))

const pulseColumn = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }

/** @type {PulseLayoutBlock} */
export const LeftColumn = styled(FlexColumn, ({ $pulse }) => ($pulse ? pulseColumn : { flex: 1, gap: '1rem' }))

/** @type {PulseLayoutBlock} */
export const RightColumn = styled('div', ({ $pulse }) => ($pulse ? pulseColumn : { flex: 1 }))

// `:empty` matters: a module that renders nothing (other energy, other client type) must not leave a gap.
/** @type {PulseLayoutBlock} */
export const LayoutSlot = styled('div', ({ $pulse }) => ($pulse ? { minWidth: 0, ':empty': { display: 'none' } } : {}))

export const SaleFormContainer = styled(FlexColumn, ({ $theme, $pulse }) => ({
  width: '100%',
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: '100%',
  boxSizing: 'border-box',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  backgroundColor: $pulse ? 'transparent' : $theme.colors.white,
}))

export const TopHeaderContainer = styled(FlexColumn, ({ $theme, $pulse }) => ({
  width: '100%',
  backgroundColor: $pulse ? 'transparent' : $theme.colors.white,
  ...($pulse && { marginTop: '14px' }),
  marginBottom: $pulse ? 0 : '0.2rem',
  flexGrow: 1,
}))

export const TopHeaderBar = styled(FlexBetween, ({ $pulse }) => ({
  padding: $pulse ? 0 : '0 0 0 0.6rem',
  gap: '1rem',
  ...($pulse && { minHeight: '38px', flexWrap: 'wrap', rowGap: '8px' }),
}))

export const TopHeaderMessages = styled(FlexColumn, ({ $pulse }) => ({
  alignItems: $pulse ? 'flex-start' : 'center',
  width: '100%',
  gap: $pulse ? '8px' : '0.25rem',
  marginTop: $pulse ? 0 : '0.5rem',
  ...($pulse && { ':not(:empty)': { marginTop: '12px' } }),
}))

export const Breadcrumb = styled(FlexCenterY, ({ $theme, $pulse }) => ({
  fontSize: '14px',
  color: $theme.colors.textMuted,
  flexShrink: 0,
  ...($pulse && { gap: '2px', flexWrap: 'wrap' }),
}))

const pulseStepColors = ($theme, $tone) => {
  if ($tone === 'done') return { backgroundColor: $theme.colors.chipOkBg, color: $theme.colors.chipOkText }
  if ($tone === 'current') {
    return {
      backgroundColor: $theme.colors.white,
      color: $theme.colors.primaryPulse,
      boxShadow: $theme.lighting.shadowPulseTab,
    }
  }
  if ($tone === 'error') return { backgroundColor: $theme.colors.dangerPulseBg, color: $theme.colors.dangerPulseText }
  return { backgroundColor: 'transparent', color: $theme.colors.textPulseMuted, fontWeight: 500 }
}

export const BreadcrumbItem = styled('div', ({ $theme, $backgroundColor, $color, $pulse, $tone }) =>
  $pulse
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        height: '28px',
        padding: '0 10px',
        borderRadius: '9999px',
        fontSize: '12.5px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...pulseStepColors($theme, $tone),
      }
    : {
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '14px',
        fontWeight: 600,
        backgroundColor: $backgroundColor || $theme.colors.bgWashAlt,
        color: $color || $theme.colors.textMedium,
      },
)
